import { isDnf } from "model/Match/DriverTime";
import { Scores } from "model/Scores";
import { getPointsDistribution } from "model/Match/Set/CupMode/Format/CupModePoints";
import { CupModeRound } from "model/Match/Set/CupMode/Track/CupModeRound";
import { CupModeRoundResult } from "model/Match/Set/CupMode/Track/CupModeRoundResult";
import { getPoints } from "model/Match/Set/PointsDistribution";
import { TrackRound } from "model/Match/Track/TrackRound";
import {
  Roster,
  getRosterId,
  getRosterDriverIds,
  getNumPlayingRosters,
} from "model/Roster/Roster";
import { RosterScope } from "model/Roster/RosterScope";
import {
  getRosterStartingPoints,
  ReverseCupModeFormat,
} from "model/Match/Set/CupMode/Format/ReverseCupModeFormat";

export const mapReverseCupModeRounds = (
  baseRounds: TrackRound[],
  rosters: Roster[],
  rosterScope: RosterScope,
  runningScores: Scores,
  format: ReverseCupModeFormat,
  lostDriverIds: string[],
  lostRosterIds: string[]
) => {
  return baseRounds.map((r) =>
    mapReverseCupModeRound(
      r,
      rosters,
      rosterScope,
      runningScores,
      format,
      lostDriverIds,
      lostRosterIds
    )
  );
};

const mapReverseCupModeRound = (
  base: TrackRound,
  rosters: Roster[],
  rosterScope: RosterScope,
  runningScores: Scores,
  format: ReverseCupModeFormat,
  lostDriverIds: string[],
  lostRosterIds: string[]
): CupModeRound => {
  if (!format.lastChance) {
    return {
      ...base,
      results: [],
    };
  }

  const numDriversAtStart =
    getNumPlayingRosters(rosters, false) * format.driversPerTeam;
  const numDriversLeft = numDriversAtStart - lostDriverIds.length;

  const pointsDist = getPointsDistribution(format.points, numDriversLeft);
  if (numDriversLeft <= 1 || !pointsDist) {
    return {
      ...base,
      results: [],
    };
  }

  const results: CupModeRoundResult[] = [];
  const lostRosters: { id: string; bestPos: number; pointsDelta: number }[] =
    [];

  for (let i = 0; i < rosters.length; i++) {
    const roster = rosters[i];
    const rosterId = getRosterId(roster);
    if (!rosterId || lostRosterIds.includes(rosterId)) {
      continue;
    }

    const rosterDriverIds = getRosterDriverIds(roster, rosterScope) ?? [];
    const rosterTimes = base.times.filter((t) =>
      rosterDriverIds.includes(t.driverId)
    );

    if (rosterTimes.length === 0) {
      continue;
    }

    const rosterPointsAtStart =
      runningScores[rosterId] ?? getRosterStartingPoints(format, rosterId);
    const rosterPointsDelta = rosterTimes.reduce(
      (a, b) => a + getPoints(pointsDist, b),
      0
    );

    if (rosterPointsAtStart <= 0) {
      const rosterPositions = rosterTimes.map((t) => t.position);

      if (format.saviourMode) {
        const rosterSafe = isSafe(numDriversLeft, rosterPositions);

        if (!rosterSafe) {
          rosterDriverIds.forEach((id) => {
            if (!lostDriverIds.includes(id)) {
              lostDriverIds.push(id);
            }
          });

          lostRosters.push({
            id: rosterId,
            bestPos: Math.min(...rosterPositions),
            pointsDelta: rosterPointsDelta,
          });

          continue; // Roster is out, deal with results after all rosters checked
        }
      } else {
        // Not saviour mode
        rosterTimes.forEach((time) => {
          if (isDnf(time) || time.position === numDriversLeft) {
            if (!lostDriverIds.includes(time.driverId)) {
              lostDriverIds.push(time.driverId);
            }
          }
        });

        const rosterLostDriverIds = lostDriverIds.filter((id) =>
          rosterDriverIds.includes(id)
        );

        if (
          rosterLostDriverIds.length >=
          (format.requiredTeamLosers ?? format.driversPerTeam)
        ) {
          rosterTimes
            .flatMap((t) => t.driverId)
            .forEach((id) => {
              if (!lostDriverIds.includes(id)) {
                lostDriverIds.push(id);
              }
            });

          lostRosters.push({
            id: rosterId,
            bestPos: Math.min(...rosterPositions),
            pointsDelta: rosterPointsDelta,
          });

          continue; // Roster is out, deal with results after all rosters checked
        }
      }
    }

    // If we get here the roster is not out, add their round result
    const rosterPointsAtEnd = Math.max(
      rosterPointsAtStart + rosterPointsDelta,
      0
    );

    results.push({
      pointsAtEnd: rosterPointsAtEnd,
      pointsAtStart: rosterPointsAtStart,
      pointsDelta: rosterPointsDelta,
      rosterId,
    });

    runningScores[rosterId] = rosterPointsAtEnd;
  }

  const sortedLostRosters = lostRosters.sort((a, b) => b.bestPos - a.bestPos);

  // Add results for all rosters that are out. Sorted descending so earlier is worst pos
  for (let i = 0; i < sortedLostRosters.length; i++) {
    const { id: rosterId, pointsDelta } = sortedLostRosters[i];
    // Limit to -1 to ensure shows as lost
    const pointsAtEnd = 0 - Math.max(rosters.length - lostRosterIds.length, 1);
    results.push({
      pointsAtEnd,
      pointsAtStart: 0,
      pointsDelta,
      rosterId,
    });

    lostRosterIds.push(rosterId);
    runningScores[rosterId] = pointsAtEnd;
  }

  return {
    ...base,
    results,
  };
};

const DANGER_ZONE = 2;
const isSafe = (numDrivers: number, positions: number[]) => {
  if (positions.length === 0) {
    return true;
  }

  const worstPos = Math.max(...positions);
  const safeThreshold = numDrivers - DANGER_ZONE;
  if (worstPos <= safeThreshold) {
    return true;
  }

  const bestPos = Math.min(...positions);
  const saviourThreshold = numDrivers / 2 - 1;
  return bestPos <= saviourThreshold;
};
